import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { deltaPct, formatKrw, formatNum } from '../lib/format';

export function Dashboard() {
  const summary = useQuery({
    queryKey: ['admin', 'cost', 'summary'],
    queryFn: adminApi.costSummary,
  });

  const s = summary.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-text-primary">대시보드</h1>
        <p className="text-sm text-text-muted mt-1">서비스 운영 핵심 지표.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          label="오늘 비용"
          value={s ? formatKrw(s.today_cost, true) : '—'}
          delta={s ? deltaPct(s.today_cost, s.yesterday_cost) : null}
          sub={s ? `어제 ${formatKrw(s.yesterday_cost, true)}` : ''}
          href="/cost"
        />
        <Card
          label="이번 달"
          value={s ? formatKrw(s.this_month_cost, true) : '—'}
          delta={s ? deltaPct(s.this_month_cost, s.last_month_cost) : null}
          sub={s ? `지난달 ${formatKrw(s.last_month_cost, true)}` : ''}
          href="/cost"
        />
        <Card
          label="오늘 호출 수"
          value={s ? formatNum(s.today_events) : '—'}
          sub={s ? `이번달 ${formatNum(s.this_month_events)}` : ''}
          href="/cost"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/users" className="card hover:bg-surface-elev transition-colors block">
          <div className="text-xs uppercase tracking-wide text-text-muted">회원 →</div>
          <div className="text-sm text-text-primary mt-2">목록 / 검색 / 상세</div>
        </Link>
        <Link to="/cost" className="card hover:bg-surface-elev transition-colors block">
          <div className="text-xs uppercase tracking-wide text-text-muted">비용 →</div>
          <div className="text-sm text-text-primary mt-2">소스별 시계열 / Top 유저 / 기능별 breakdown</div>
        </Link>
      </div>
    </div>
  );
}

function Card({
  label,
  value,
  delta,
  sub,
  href,
}: {
  label: string;
  value: string;
  delta?: { pct: number; sign: 'up' | 'down' | 'flat' } | null;
  sub?: string;
  href?: string;
}) {
  const inner = (
    <>
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="text-2xl font-semibold text-text-primary mt-2">{value}</div>
      <div className="mt-1 flex items-baseline gap-2">
        {delta && (
          <span
            className={
              delta.sign === 'up'
                ? 'text-xs text-warning'
                : delta.sign === 'down'
                  ? 'text-xs text-success'
                  : 'text-xs text-text-muted'
            }
          >
            {delta.sign === 'up' ? '▲' : delta.sign === 'down' ? '▼' : '·'} {delta.pct.toFixed(0)}%
          </span>
        )}
        {sub && <span className="text-xs text-text-muted">{sub}</span>}
      </div>
    </>
  );
  if (href) {
    return (
      <Link to={href} className="card hover:bg-surface-elev transition-colors block">
        {inner}
      </Link>
    );
  }
  return <div className="card">{inner}</div>;
}
